import React, { useState } from 'react';

import { Map as MapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

// The frame of every public page behind a member's share link: a shared Courses Played list
// (SharedCoursesPlayed) and a shared profile (SharedProfile). Charcoal and gold, like the public
// course page, a logo bar on top, and a floating call to action for a visitor who likes what they
// see.

// Where the floating call to action sends a visitor: the app's front door.
const APP_URL = 'https://my.golfplayed.com';

// "Andrew G." to "AG": what stands in for the owner's picture when they have none, or it fails.
const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((part) => part.charAt(0))
    // A letter has two cases; a stray "(" or digit does not.
    .filter((letter) => letter.toLowerCase() !== letter.toUpperCase())
    .join('')
    .toUpperCase()
    .slice(0, 2);

export const OwnerAvatar: React.FC<{
  name: string;
  pictureUrl?: string;
  // Size and initials size. The picture fills it.
  className?: string;
}> = ({ name, pictureUrl, className = 'h-12 w-12 text-base' }) => {
  const [failed, setFailed] = useState(false);

  return (
    <span
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-charcoal-light font-display font-bold text-primary ring-2 ring-primary ${className}`}
    >
      {pictureUrl && !failed ? (
        <img
          src={pictureUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
          draggable={false}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
};

export const MessageCard: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <div className="rounded-xl border border-white/10 bg-charcoal-light px-6 py-10 text-center">
    <h1 className="text-lg font-semibold">{title}</h1>
    <p className="mt-2 text-sm text-white/60">{body}</p>
    <Link
      to="/explore"
      className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-secondary transition hover:bg-opacity-90"
    >
      <MapIcon className="h-4 w-4" />
      Explore the map
    </Link>
  </div>
);

// showCallToAction is on once the page has loaded its content. The bottom padding then keeps the
// last of it clear of the floating call to action.
export const SharedPageLayout: React.FC<{
  showCallToAction: boolean;
  children: React.ReactNode;
}> = ({ showCallToAction, children }) => (
  <div className="min-h-screen bg-charcoal text-white">
    {/* Pinned, so the logo stays in view down a long page. */}
    <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/10 bg-charcoal-light px-4 py-3">
      <Link to="/explore" aria-label="GolfPlayed, the course map">
        <img
          src="/images/logo-white.png"
          alt="GolfPlayed"
          className="h-7 w-36 object-contain object-left"
        />
      </Link>
    </header>

    <main
      className={`mx-auto w-full max-w-xl px-4 pt-6 sm:pt-10 ${
        showCallToAction
          ? 'pb-[calc(8rem_+_env(safe-area-inset-bottom))]'
          : 'pb-6 sm:pb-10'
      }`}
    >
      {children}
    </main>

    {/* The way in for a visitor. Floats over the page, clear of the phone's home bar. Black on
        gold, as BRAND.md requires for text on a gold fill. */}
    {showCallToAction && (
      <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
        <a
          href={APP_URL}
          className="mx-auto block max-w-xl rounded-2xl bg-primary px-4 py-3 text-center text-secondary shadow-lg shadow-black/40 transition hover:bg-opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <span className="block font-display text-sm font-semibold">
            How many courses have you ever played?
          </span>
          <span className="mt-0.5 block font-display text-2xl font-extrabold uppercase tracking-wide">
            Find out here
          </span>
        </a>
      </div>
    )}
  </div>
);
